import { useEffect, useRef } from "react";
import { Loader2 } from "@semo/icons";
import { cn } from "../../utils";
import {
  NotificationItemBase,
  NotificationItemBaseProps,
} from "./NotificationItemBase";

export interface NotificationListBaseProps {
  compact?: boolean;
  className?: string;
  notifications: NotificationItemBaseProps[];
  isLoading?: boolean;
  enableInfiniteScroll?: boolean;
  isFetchingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onItemClick?: (
    resourceType?: string,
    resourceId?: string,
    id?: string,
    isRead?: boolean,
  ) => void;
}

export const NotificationListBase = ({
  compact = false,
  className,
  notifications,
  isLoading = false,
  enableInfiniteScroll = false,
  isFetchingMore = false,
  hasMore = false,
  onLoadMore,
  onItemClick,
}: NotificationListBaseProps) => {
  const observerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!enableInfiniteScroll || !onLoadMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting && hasMore && !isFetchingMore) {
          onLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [enableInfiniteScroll, onLoadMore, hasMore, isFetchingMore]);

  return (
    <div
      className={cn(
        "space-y-1",
        compact &&
          "max-h-[400px] overflow-y-auto px-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-slate-200/50 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-slate-300/80 transition-colors",
        className,
      )}
    >
      {isLoading ? (
        <div
          data-testid="notification-list-loader"
          className="flex items-center justify-center py-6"
        >
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-6 text-xs text-slate-400">
          알림이 없습니다
        </div>
      ) : (
        <>
          {notifications.map((noti, index) => (
            <NotificationItemBase
              key={noti.id ?? index}
              {...noti}
              onClick={onItemClick}
            />
          ))}

          {enableInfiniteScroll && isFetchingMore && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
            </div>
          )}

          {enableInfiniteScroll && !hasMore && notifications.length > 0 && (
            <div className="text-center py-4 text-xs text-slate-400">
              모든 알림을 확인했습니다
            </div>
          )}

          {enableInfiniteScroll && <div ref={observerRef} className="h-2" />}
        </>
      )}
    </div>
  );
};
